import { Alert, Button, Flex, Input, Select } from "antd";
import { CheckCircleFilled, CloseCircleFilled, LoadingOutlined } from "@ant-design/icons";
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useViewerStore } from "../../../../../store/store";
import siteService from "../../../../../services/siteService";
import { getDesignVersionList } from "../../../../../services/designVersionService";
import { useCurrentUser } from "../../../../../contexts/UserContext";
import { CaretDownIcon } from "@phosphor-icons/react";
import {
    PRODUCTION_CALCULATION_TYPES,
    SHADE_CALCULATIONS,
    siteCalculationState,
} from "../../../../../services/siteCalculationState";
import { generateDirectLayoutInBox, uploadToBox, uploadToBoxDirectly } from "../../../../../services/documentService";
import { isDirectLayoutAutomateEnabled } from "../../../../../services/userCapabilityService";
import {
    BoxUploadFile,
    boxFileFromImageUrl,
    generateBoxFileWithRetry,
    generateBoxFilesWithRetry,
    isGrayscaleTopviewFile,
    isValidBoxUploadFile,
} from "../../../../../helpers/boxUpload";
import { LoadingContext } from "../../../../../store/loadingMessageStore";
import { generateDirectLayout } from "../../Drawer/Drawer-Sections/5-Reports-Section/Reports/Direct_Layout";
import { generateMeasureLayout } from "../../Drawer/Drawer-Sections/5-Reports-Section/Reports/Measure_Layout";
import { generateSiteReport } from "../../Drawer/Drawer-Sections/5-Reports-Section/Reports/Site_Report";
import { isBoxUploadEnabledForOpportunity } from "../../../../../helpers/featureFlags";
import { logger } from "../../../../../services/loggingService";
import { UnitContext } from "../../../../../contexts/UnitContext";
import treeEPCService from "../../../../../services/treeEPCService";
import { useSiteCalculationBlockedStatus } from "../../../../../hooks/calculationStateHook";
import { useGetSaveDesignData } from "../../../../../hooks/useGetSaveDesignData";
import { ExportGen } from "../../Three-JS-Viewer/Controllers/Common";
import { uploadCustomMap } from "../../Three-JS-Viewer/Controllers/MapControl";
import ProductionValuesValidation from "./ProductionValuesValidation";
import {AlertType, CalculationType, ImageryOptions, ScreenShotType} from "../../../../../@types/enums";
import { dismissLightReachPitchWarningIfShowing } from "../../../../../helpers/lightReachWarning";
import { useOneButtonStore } from "../../../../../store/oneButtonStore";
import {Buffer} from 'buffer';
import quoteServices from "../../../../../services/oneButtonServices/quoteServices";
import { UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD } from "../../oneBUTTONDashboard/BatteryDetailsModal";
import { runRefreshSystemDesignAfterFinalizeAction } from "../../oneBUTTONDashboard/dashboardActionRefreshSystemDesign";
import {
    beginFinalizeModuleGraph,
    endFinalizeModuleGraph,
} from "../../Three-JS-Viewer/Controllers/TreeImpactEligibility";
import { resolveEffectiveAnnualUsageKwh } from "../../../../../helpers/utilityMonthlyDailyToAnnual";
window.Buffer = window.Buffer || Buffer

type FileUploadStatus = {
    name: string;
    percent: number;
    state: "uploading" | "processing" | "done" | "failed";
};

const renderBoxUploadFileList = (fileStatuses: FileUploadStatus[]) => (
    <div className="box-upload-file-list">
        {fileStatuses.map((status, idx) => {
            const isDone = status.state === "done";
            const isFailed = status.state === "failed";
            const isProcessing = status.state === "processing" || status.percent >= 100;
            return (
                <div key={`${status.name}-${idx}`} className="box-upload-file-list__row">
                    <span className="box-upload-file-list__icon">
                        {isDone ? (
                            <CheckCircleFilled style={{ color: "#52c41a" }} />
                        ) : isFailed ? (
                            <CloseCircleFilled style={{ color: "#ff4d4f" }} />
                        ) : (
                            <LoadingOutlined spin />
                        )}
                    </span>
                    <span className="box-upload-file-list__name">{status.name}</span>
                    <span className="box-upload-file-list__meta">
                        {isDone
                            ? "Done"
                            : isFailed
                              ? "Failed"
                              : isProcessing
                                ? "Processing"
                                : `${status.percent}%`}
                    </span>
                </div>
            );
        })}
    </div>
);

const FINALIZE_CALC_WAIT_MS = 120_000;

async function waitWhile(predicate: () => boolean, maxMs = FINALIZE_CALC_WAIT_MS): Promise<void> {
    const started = Date.now();
    while (predicate()) {
        if (Date.now() - started > maxMs) {
            logger.warn("Finalize prep timed out waiting for calculations");
            return;
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
    }
}

export interface ValidationError {
    roofID: string | number;
    field: "efficiency" | "productionActual" | "productionUtil" | "solarAccess";
    badValue: unknown;
}

interface ProductionValues {
    roofID: string | number;
    efficiency: number;
    productionActual: number;
    productionUtil: number;
    solarAccess: number;
}


const showSystemDesignRefreshLoader = (sceneViewer: { viewerAlert: (opts: object) => void }): void => {
    sceneViewer.viewerAlert({
        show: true,
        isModal: true,
        title: "Refreshing System Design",
        message: (
            <>
                <Alert
                    style={{ color: "var(--textColor)" }}
                    message="Syncing your saved design with oneBUTTON. Please wait..."
                    type="info"
                    showIcon
                />
                <br />
                <Flex style={{ textAlign: "center" }} gap={10} align="center" justify="center">
                    <span className="loader-icon-spinner " />
                    <p className="theme-based-text-color">Refreshing system design...</p>
                </Flex>
            </>
        ),
    });
};

const waitForSystemDesignRefreshComplete = (): Promise<void> =>
    new Promise((resolve) => {
        const finish = () => {
            window.clearTimeout(timeoutId);
            unsub();
            resolve();
        };

        const timeoutId = window.setTimeout(finish, 120_000);
        const unsub = useOneButtonStore.subscribe((state, prev) => {
            if (prev.systemDesignRefreshing && !state.systemDesignRefreshing) {
                finish();
            }
        });

        if (!useOneButtonStore.getState().systemDesignRefreshing) {
            finish();
        }
    });

const FinaliseConfirm: FC = () => {
    const unitContext = useContext(UnitContext);
    const { hasCapability } = useCurrentUser();

    const [saving, setSaving] = useState<boolean>(false);
    const [annualUsageInput, setAnnualUsageInput] = useState<number | null>(null);
    const [epcEstimatedProductionInput, setEpcEstimatedProductionInput] =
        useState<number | null>(null);

    const {
        sceneViewer,
        designID,
        designList,
        isInActiveDesign,
        isAiGeneratedDesign,
        setbackNotes,
        shadeCalculatingIdx,
        shadeCalculatingLength,
        annualUsage,
        SFannualUsage,
        epcEstimatedProduction,
        imageryType,
        customImageFile,
        refreshingShade,
        loadingCompleted,
        productType,
        detachedStructureBuildingIds,
        rsaNotesFromDesign,
        customerSiteRestrictions,
        rsaStatusFromDesign,
    } = useViewerStore((state) => state);

    const [rsaNotes, setRsaNotes] = useState<string>(() => sanitizeRsaNotesInput(rsaNotesFromDesign ?? ''));
    const [rsaStatus, setRsaStatus] = useState<string | undefined>(); // rsaStatusFromDesign

    const handleCustomerSiteRestrictionsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        useViewerStore.setState({ customerSiteRestrictions: e.target.value });
    };
    const isOpportunityRRorRRR = productType.includes('RRR') || productType.includes('R&R');

    const solarUtilityData = useOneButtonStore((s) => s.solarUtilityData);
    const useOneButtonAnnualInput = hasCapability('Show_OneButton');
    const effectiveAnnualUsageKwh = useMemo(() => {
        return resolveEffectiveAnnualUsageKwh({
            useOneButtonAnnualInput,
            oneButtonAnnualUsageOverride: annualUsage,
            solarUtility: solarUtilityData?.SolarUtility as Record<string, unknown> | undefined,
            sfAnnualUsage: SFannualUsage,
        });
    }, [useOneButtonAnnualInput, annualUsage, solarUtilityData, SFannualUsage]);

    const [finalizePrepReady, setFinalizePrepReady] = useState(false);
    const hasUserEditedAnnualUsage = useRef(false);

    const notReady: boolean = !finalizePrepReady || refreshingShade;
    const calculationBlockedMessage = useSiteCalculationBlockedStatus({
        loadingCompleted,
        refreshingShade,
        productionValuesReady: finalizePrepReady,
        shadeCalculatingIdx: shadeCalculatingIdx + 1,
        shadeCalculatingLength,
    });

    useEffect(() => {
        if (!loadingCompleted) return;

        let cancelled = false;

        const runFinalizePrep = async (): Promise<void> => {
            beginFinalizeModuleGraph();
            try {
                siteCalculationState.needsUpdate(SHADE_CALCULATIONS);
                await siteCalculationState.useCalculations(SHADE_CALCULATIONS);

                await waitWhile(() => useViewerStore.getState().refreshingShade);

                await waitWhile(
                    () =>
                        PRODUCTION_CALCULATION_TYPES.some((type) =>
                            siteCalculationState.doesNeedUpdateOrUpdating(type),
                        ) || siteCalculationState.doesNeedUpdateOrUpdating(SHADE_CALCULATIONS),
                );
            } catch (err) {
                logger.error(err, "Finalize calculation prep failed");
            } finally {
                endFinalizeModuleGraph();
                if (!cancelled) setFinalizePrepReady(true);
            }
        };

        setFinalizePrepReady(false);
        void runFinalizePrep();

        return () => {
            cancelled = true;
            endFinalizeModuleGraph();
        };
    }, [loadingCompleted]);

    useEffect(() => {
        if (hasUserEditedAnnualUsage.current) return;

        if (effectiveAnnualUsageKwh != null && effectiveAnnualUsageKwh > 0) {
            setAnnualUsageInput(effectiveAnnualUsageKwh);
        }
        if (epcEstimatedProduction) {
            setEpcEstimatedProductionInput(epcEstimatedProduction);
        }
    }, [effectiveAnnualUsageKwh, epcEstimatedProduction]);

    const handleAnnualUsageInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        hasUserEditedAnnualUsage.current = true;
        const raw = e.target.value;
        if (raw === '') {
            setAnnualUsageInput(null);
            if (useOneButtonAnnualInput) {
                useViewerStore.setState({ annualUsage: null });
            }
            return;
        }

        const parsed = Number(raw);
        if (!Number.isFinite(parsed)) return;

        setAnnualUsageInput(parsed);
        if (useOneButtonAnnualInput) {
            useViewerStore.setState({ annualUsage: parsed > 0 ? parsed : null });
        }
    };

    const getPayload = useGetSaveDesignData(
        annualUsageInput,
        epcEstimatedProductionInput,
        rsaStatus,
        rsaNotes,
    );
    const usesOneDocDirectLayout = () =>
        hasCapability("Direct_Layout") &&
        (hasCapability("Direct_Layout_Automate") || isDirectLayoutAutomateEnabled());

    const handleRsaNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setRsaNotes(sanitizeRsaNotesInput(e.target.value));
    };

    function sanitizeRsaNotesInput(value: string, maxLength = 255): string {
        return value.replace(/[\p{Cf}\u0000-\u001F\u007F]|[^\p{L}\p{N}\s.,'\-:()/]/gu, '').slice(0, maxLength);
    }


    const rsaStatusOptions = [
        { label: "RSA Complete", value: "RSA Complete" },
        { label: "RSA: Estimate Shade", value: "RSA: Estimate Shade" },
        { label: "RSA: Estimated Layout", value: "RSA: Estimated Layout" },
        { label: "RSA: Full Estimate", value: "RSA: Full Estimate" },
        { label: "Needs Measure", value: "Needs Measure" },
        { label: "EV Not Available", value: "EV Not Available" },
        { label: "RSA Not Viable", value: "RSA Not Viable" },
        { label: "Unable to ID Home", value: "Unable to ID Home" },
        { label: "Aurora Estimate", value: "Aurora Estimate" },
        { label: "Aurora Complete", value: "Aurora Complete" },
        { label: "oneDRAW Estimate", value: "oneDRAW Estimate" },
        { label: "oneDRAW Complete", value: "oneDRAW Complete" },
    ];


    const validateProductionValues = (
        payload: Awaited<ReturnType<typeof getPayload>>,
    ): { goodRoofs: ProductionValues[]; errors: ValidationError[] } => {
        const goodRoofs: ProductionValues[] = [];
        const errors: ValidationError[] = [];

        const { designDetails } = payload;
        const { buildingDetails } = designDetails[0] ?? {};

        if (!buildingDetails?.length) {
            return {
                goodRoofs,
                errors: [
                    { roofID: "N/A", field: "efficiency", badValue: "No building details" },
                ],
            };
        }

        buildingDetails.forEach(({ roofDetails }: any) => {
            roofDetails.forEach((roof: any) => {
                const { effic, prodActual, prodUtil, solAcc, tag_Point: roofID } = roof;

                const checks: Array<[ValidationError["field"], unknown]> = [
                    ["efficiency", effic],
                    ["productionActual", prodActual],
                    ["productionUtil", prodUtil],
                    ["solarAccess", solAcc],
                ];

                let roofIsValid = true;
                checks.forEach(([field, value]) => {
                    if (value === undefined || value === null || Number.isNaN(value)) {
                        roofIsValid = false;
                        errors.push({ roofID, field, badValue: value });
                    }
                });

                if (roofIsValid) {
                    goodRoofs.push({
                        roofID,
                        efficiency: effic,
                        productionActual: prodActual,
                        productionUtil: prodUtil,
                        solarAccess: solAcc,
                    });
                }
            });
        });

        return { goodRoofs, errors };
    };

    function showEfficiencyOverridePrompt(): Promise<boolean> {
        return new Promise((resolve) => {
            sceneViewer.viewerAlert({
                show: true,
                title: "Efficiency Validation Error",
                message: (
                    <>
                        <p style={{marginBottom: '1rem'}}>Efficiency is minimally greater than solar access. Would you like to override the efficiency to match the solar access and continue finalization?</p>
                        <p><strong>Efficiency: </strong>{useViewerStore.getState().efficiency}</p>
                        <p><strong>Solar Access: </strong>{useViewerStore.getState().weightedSolarAverage}</p>
                        <Alert 
                            style={{color: 'black', fontSize: '12px', marginTop: '1rem'}}
                            message="Pressing Confirm will continue the finalization process, while pressing Cancel will stop it."
                            type='warning'
                            showIcon
                        />
                        <div style={{ display: "flex", gap: "10px", marginTop: "1rem" }}>
                            <Button
                                size="middle"
                                type="primary"
                                className="editor__control--finalize modal-confirm-button"
                                onClick={() => {
                                    useViewerStore.setState({efficiencyOverride: useViewerStore.getState().weightedSolarAverage as number})
                                    sceneViewer.viewerAlert({ show: false });
                                    resolve(true); // Confirm
                                }}
                            >
                                Confirm
                            </Button>
                            <Button
                                size="middle"
                                type="primary"
                                className="editor__control--finalize modal-cancel-button"
                                onClick={() => {
                                    sceneViewer.viewerAlert({ show: false });
                                    resolve(false); // Cancel
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                ),
                messageType: AlertType.warning,
                isModal: true,
            });
        });
    }

    const syncOneButtonDesignAfterSave = async (): Promise<void> => {
        /** Refresh oneBUTTON System Design after save settles — must finish before success alerts. */
        const { opportunityID, opportunityDetails } = useOneButtonStore.getState();
        const resolvedOpportunityId = String(
            opportunityID ?? opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId ?? "",
        ).trim();
        if (!resolvedOpportunityId) return;

        /** Show refresh loader immediately so there is no gap after the finalize/save loader. */
        let refreshLoaderShown = true;
        showSystemDesignRefreshLoader(sceneViewer);

        const hideRefreshLoader = () => {
            if (!refreshLoaderShown) return;
            refreshLoaderShown = false;
            sceneViewer.viewerAlert({ show: false });
        };
        const unsubRefreshLoader = useOneButtonStore.subscribe((state, prev) => {
            if (!prev.systemDesignRefreshing && state.systemDesignRefreshing) {
                refreshLoaderShown = true;
                showSystemDesignRefreshLoader(sceneViewer);
            }
            if (prev.systemDesignRefreshing && !state.systemDesignRefreshing) {
                hideRefreshLoader();
            }
        });

        try {
            await quoteServices.contractUpdateLoanStatus(
                resolvedOpportunityId,
                UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD,
                0,
            );
            await new Promise((resolve) => setTimeout(resolve, 3000));

            await runRefreshSystemDesignAfterFinalizeAction({ opportunityId: resolvedOpportunityId });
            await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
            await waitForSystemDesignRefreshComplete();
        } finally {
            unsubRefreshLoader();
            hideRefreshLoader();
        }
    };

    const saveDesignHandler = async (): Promise<void> => {
        const salesForceID = sessionStorage.getItem("SalesForceId");
        const totalSystemSize = useViewerStore.getState().totalSystemSize as number | undefined;

        // Validate total system size before proceeding
        if (!isOpportunityRRorRRR && (!totalSystemSize || Number.isNaN(totalSystemSize) || totalSystemSize === 0)) {
            logger.error("Finalization Blocked : Total system size is invalid:", totalSystemSize);
            sceneViewer.viewerAlert({
                show: true,
                title: "Production Data Validation Error",
                message: (
                    <ProductionValuesValidation errors={["Total System Size is  0 , Empty Module Layout"]} />
                ),
                messageType: AlertType.error,
                isModal: true,
            });
            return;
        }

        const payload = await getPayload();

        const { goodRoofs: productionValuesArray, errors } = validateProductionValues(payload);

        if (errors.length) {
            errors.forEach((e) =>
                logger.error(
                    `Finalization Blocked : Roof ${e.roofID} ► ${e.field} is invalid (value: ${String(
                        e.badValue,
                    )})`,
                    {},
                ),
            );

            const errorMessages = errors.map(
                (e) => `Roof ${e.roofID}: ${e.field} is invalid (value: ${String(e.badValue) || "null/undefined"})`,
            );

            sceneViewer.viewerAlert({
                show: true,
                title: "Production Data Validation Error",
                message: <ProductionValuesValidation errors={errorMessages} />,
                messageType: AlertType.error,
                isModal: true,
            });
            return;
        }

        const data  = await siteService.saveSiteDetails(payload);

        if (data?.Message === "Error occured during the saving of records. Contact Administrator") {
            sceneViewer.viewerAlert({
                show: true,
                title: "Save Failed",
                message: "Error occured during the saving of records. Contact Administrator",
                messageType: AlertType.error,
                isModal: false,
            });
            return;
        }

        logger.info(data, "saved design data");
        const DesignNumber = data.design[0]?.Design_No;
        const VersionNumber = data.design[0]?.Version_No;
        const boxUploadEnabled = isBoxUploadEnabledForOpportunity(salesForceID);

        const refreshDesignList = async () => {
            const designListNew = await getDesignVersionList(salesForceID as string);
            if (designListNew) {
                useViewerStore.setState({designList : designListNew});
                useViewerStore.setState({selectedDesignIndex : 0});
                useViewerStore.setState({selectedSiteDesign : null});
                useViewerStore.setState({ isRoofLabelOverrideByUser: false });
            }
        };

        if (!boxUploadEnabled) {
            await refreshDesignList();
        }

        if (
            designList &&
            designList[0]?.Design_Certification_Status &&
            !["Not Certified", "Certified Design Override", null].includes(
                designList[0]?.Design_Certification_Status,
            )
        ) {
            await siteService.saveDesignCertification(data.design[0].Id, 4);
        }

        let boxUploadFiles: BoxUploadFile[] = [];
        let failedReportLabels: string[] = [];

        if (boxUploadEnabled) {
            sceneViewer.viewerAlert({ show: false });
            const reportGeneration = await generateAllReports(
                annualUsageInput,
                epcEstimatedProductionInput,
            );
            boxUploadFiles = reportGeneration.files;
            failedReportLabels = reportGeneration.failedReports;
        }

        useViewerStore.setState({
            designID: data.design[0]?.Id,
            isInActiveDesign: false,
            isSavingInProgress: false,
        });

        if (DesignNumber && VersionNumber) saveEpcEstimate(DesignNumber, VersionNumber);
        if (imageryType === ImageryOptions.CUSTOM && customImageFile)
            uploadCustomMap(customImageFile);

        dismissLightReachPitchWarningIfShowing();
        if (hasCapability("Show_OneButton")) await syncOneButtonDesignAfterSave();

        if (boxUploadEnabled) {
            if (failedReportLabels.length > 0) {
                sceneViewer.viewerAlert({
                    show: true,
                    title: "Box Upload: Report Generation Failed",
                    message: `Design Number : ${DesignNumber} & Version Number : ${VersionNumber}\n\nDesign was saved, but these files could not be generated for Box upload after 3 attempts:\n${failedReportLabels.join("\n")}`,
                    messageType: AlertType.error,
                    isModal: false,
                });
            }

            if (boxUploadFiles.length > 0 || usesOneDocDirectLayout()) {
                let uploadedFiles: string[] = [];
                let notUploaded: string[] = [];

                const { cancelledRemainingUploads } = await uploadAllFiles(
                    salesForceID,
                    boxUploadFiles,
                    failedReportLabels,
                    DesignNumber,
                    VersionNumber,
                    refreshDesignList,
                    async (uploaded, failed) => {
                        uploadedFiles = uploaded;
                        notUploaded = failed;
                    },
                    (error) => {
                        sceneViewer.viewerAlert({
                            show: true,
                            title: "Design Saved & Error while uploading files to Box",
                            message: `Design Number : ${DesignNumber} & Version Number : ${VersionNumber}\n\n ${error}`,
                            messageType: AlertType.error,
                            isModal: false,
                        });
                    },
                );

                if (cancelledRemainingUploads) {
                    sceneViewer.viewerAlert({
                        show: true,
                        title: "Box Upload Failed",
                        message: `Design Number : ${DesignNumber} & Version Number : ${VersionNumber}\n\n Unable to upload files to Box. Please retry Finalize.`,
                        messageType: AlertType.error,
                        isModal: false,
                    });
                    return;
                }

                const allUploaded =
                    notUploaded.length === 0 &&
                    failedReportLabels.length === 0 &&
                    uploadedFiles.length > 0;

                if (allUploaded) {
                    sceneViewer.viewerAlert({
                        show: true,
                        title: "Design Saved & All Files uploaded to Box",
                        message: `Design Number : ${DesignNumber} & Version Number : ${VersionNumber}\n\n Files uploaded:\n ${uploadedFiles.join("\n")}`,
                        messageType: AlertType.success,
                        isModal: false,
                    });
                } else {
                    const failedParts: string[] = [];
                    if (failedReportLabels.length > 0) {
                        failedParts.push(
                            `Could not generate:\n${failedReportLabels.join("\n")}`,
                        );
                    }
                    if (notUploaded.length > 0) {
                        failedParts.push(`Unable to upload:\n${notUploaded.join("\n")}`);
                    }

                    sceneViewer.viewerAlert({
                        show: true,
                        title: "Design Saved & Some Files Failed to Upload to Box",
                        message: `Design Number : ${DesignNumber} & Version Number : ${VersionNumber}\n\n${failedParts.join("\n\n")}${
                            uploadedFiles.length > 0
                                ? `\n\nFiles uploaded:\n${uploadedFiles.join("\n")}`
                                : ""
                        }`,
                        messageType: AlertType.error,
                        isModal: false,
                    });
                }
            } else {
                await refreshDesignList();
                if (failedReportLabels.length === 0) {
                    sceneViewer.viewerAlert({
                        show: true,
                        title: "Box Upload: No Reports Generated",
                        message: `Design Number : ${DesignNumber} & Version Number : ${VersionNumber}\n\nDesign was saved, but no reports were generated for Box upload.`,
                        messageType: AlertType.error,
                        isModal: false,
                    });
                }
            }
        } else {
            sceneViewer.viewerAlert({
                show: true,
                title: "Design Saved Successfully",
                message: `Design Number : ${DesignNumber} & Version Number : ${VersionNumber}`,
                messageType: AlertType.success,
                isModal: false,
            });
        }
    };


    const confirm = async (): Promise<void> => {
        const solarAccess = useViewerStore.getState().weightedSolarAverage as number;
        const efficiency = useViewerStore.getState().efficiency;
        useViewerStore.setState({efficiencyOverride: null});
        const acceptableEfficiencyOverrideThreshold = 3;
        // If user has DL permission and leaves it blank, set to null so SF is not overridden
        if (hasCapability("Direct_Layout")) {
            if (!annualUsageInput) setAnnualUsageInput(null);
            if (!epcEstimatedProductionInput) setEpcEstimatedProductionInput(null);
        } else {
            // User doesn't have DL permission, ensure both values are null
            setAnnualUsageInput(null);
            setEpcEstimatedProductionInput(null);
        }

        if (efficiency > solarAccess) {
            if (efficiency - solarAccess <= acceptableEfficiencyOverrideThreshold && hasCapability("Efficiency_Override")) {
                const userConfirmed = await showEfficiencyOverridePrompt();
                if (!userConfirmed) {
                    return; // User clicked Cancel
                }
            } else {
                logger.error(
                    "Finalization Blocked: Efficiency is greater than solar access",
                    "Efficiency: " + efficiency,
                    "Solar Access: " + solarAccess
                );
                sceneViewer.viewerAlert({
                    show: true,
                    title: "Efficiency Validation Error",
                    message: (
                        <ProductionValuesValidation errors={["Efficiency is greater than solar access. Please contact the RSA team for assistance."]} />
                    ),
                    messageType: AlertType.error,
                    isModal: true,
                });
                return;
            }
        }

        sceneViewer.enableAllLayers();
        useViewerStore.setState({ isSavingInProgress: true });
        setSaving(true);
        sceneViewer.viewerAlert({ show: true, isModal: true, title: "Saving Design",
            message: 
                <>
                    <Alert
                        style={{ color: 'var(--textColor)' }}
                        message="This may take up to 30 seconds, don't refresh or quit while in progress."
                        type="info"
                        showIcon
                    />
                    <br />
                    <Flex style={{ textAlign: "center" }} gap={10} align="center" justify="center">
                        <span className="loader-icon-spinner " />
                        <p className="theme-based-text-color">
                            Saving New {designID === 0 || isInActiveDesign || isAiGeneratedDesign ? "Design" : "Version"} to the database. Please Wait...
                        </p>
                    </Flex>
                </>
        });

        try {
            await saveDesignHandler();
        } catch (ex: any) {
            logger.error(ex);
            let errorMessagesArray: string[] = [];
            let errorMessagesString = "";
            if (ex.response?.data?.Errors && Array.isArray(ex.response.data.Errors)) {
                errorMessagesArray = ex.response.data.Errors.map((err: any) => err.ErrorMessage);
                errorMessagesString = `\n\nDetails: ${errorMessagesArray.join("; ")}`;
            }
            let message = `Failed to Save: ${ ex.message + " " + errorMessagesString}`;

            /** Verbose Error extraction */
            const verboseErrorMessage = true;
            if (verboseErrorMessage) {
                try {
                    const errors = ex.response.data.errors;
                    if (errors) {
                        for (const key of Object.keys(errors)) {
                            message += `\n${key}: ${errors[key]}`;
                        }
                    }
                } catch {
                    /* ignore */
                }

                try {
                    message += `\n${ex.response.data.Message}`;
                } catch {
                    /* ignore */
                }
            }

            sceneViewer.viewerAlert({ show: false });
            useViewerStore.setState({ isSavingInProgress: false });
            sceneViewer.viewerAlert({
                show: true,
                title: "Error",
                message,
                messageType: AlertType.error,
                isModal: false,
            });
        }
    };

    const cancel = (): void => {
        sceneViewer.viewerAlert({ show: false });
    };


    const generateAllReports = async (
        annualUsageVal: number | null,
        productionVal: number | null,
    ): Promise<{ files: BoxUploadFile[]; failedReports: string[] }> => {
        const viewerState = useViewerStore.getState();
        const { siteAddress } = viewerState;
        const loadingContext = new LoadingContext();
        loadingContext.setDefaultTitle("Generating Reports...");
        const files: BoxUploadFile[] = [];
        const failedReports: string[] = [];

        const recordResult = (reportLabel: string, file: BoxUploadFile | null) => {
            if (file) {
                files.push(file);
                return;
            }
            failedReports.push(reportLabel);
        };

        try {
            loadingContext.setMessage({
                title: "Generating Files",
                message: "Generating Topview...",
            });
            const [topviewFile, grayscaleFile] = await generateBoxFilesWithRetry("Topview", async () => {
                const images = await sceneViewer.getImagesForSiteReport(
                    ScreenShotType.home,
                    { includeGrayscaleTopview: true },
                ) as { home?: string; homeGrayscale?: string };

                return Promise.all([
                    boxFileFromImageUrl(images?.home, `${siteAddress}_topview.png`, "Topview screenshot"),
                    boxFileFromImageUrl(images?.homeGrayscale, `${siteAddress}_topview_grayscale.png`, "Topview grayscale"),
                ]);
            });
            recordResult("Topview Grayscale", grayscaleFile);
            recordResult("Topview Screenshot", topviewFile);

            if (hasCapability("Direct_Layout") && !usesOneDocDirectLayout()) {
                loadingContext.setMessage({
                    title: "Generating Files",
                    message: "Generating Direct Layout...",
                });
                const file = await generateBoxFileWithRetry("Direct Layout", async () => {
                    const directLayoutPdf = await generateDirectLayout(
                        viewerState,
                        annualUsageVal,
                        productionVal,
                    );
                    if (!directLayoutPdf) {
                        return null;
                    }
                    const fileName = `${siteAddress} - DIRECT LAYOUT.pdf`;
                    const buffer = directLayoutPdf.output("arraybuffer") as ArrayBuffer;
                    return {
                        data: new Blob([buffer], { type: "application/pdf" }),
                        name: fileName,
                    };
                });
                recordResult("Direct Layout", file);
            }

            if (hasCapability("Measure_Layout")) {
                loadingContext.setMessage({
                    title: "Generating Files",
                    message: "Generating Measure Layout...",
                });
                const file = await generateBoxFileWithRetry("Measure Layout", async () => {
                    const measureLayoutPdf = await generateMeasureLayout(sceneViewer, viewerState);
                    if (!measureLayoutPdf) {
                        return null;
                    }
                    const fileName = `${siteAddress} - MEASURE LAYOUT.pdf`;
                    const measureBuffer = measureLayoutPdf.output("arraybuffer") as ArrayBuffer;
                    return {
                        data: new Blob([measureBuffer], { type: "application/pdf" }),
                        name: fileName,
                    };
                });
                recordResult("Measure Layout", file);
            }

            if (hasCapability("oneDRAW_Site_Report") || hasCapability("Genesis_Site_Report")) {
                loadingContext.setMessage({
                    title: "Generating Files",
                    message: "Generating Site Report...",
                });
                const file = await generateBoxFileWithRetry("Site Report", async () => {
                    const siteReportPdf = await generateSiteReport(
                        sceneViewer,
                        viewerState,
                        unitContext,
                    );
                    if (!siteReportPdf) {
                        return null;
                    }
                    const fileName = `${siteAddress} - SITE REPORT.pdf`;
                    return {
                        data: siteReportPdf.output("blob") as Blob,
                        name: fileName,
                    };
                });
                recordResult("Site Report", file);
            }

            if (hasCapability(".GEN")) {
                loadingContext.setMessage({
                    title: "Generating Files",
                    message: "Generating Genesis File...",
                });
                const file = await generateBoxFileWithRetry("Genesis File", async () => {
                    const exportGen = await ExportGen(sceneViewer, false);
                    if (!exportGen) {
                        return null;
                    }
                    const fileName = `${siteAddress}.genesis`;
                    return { data: exportGen as Blob, name: fileName };
                });
                recordResult("Genesis File", file);
            }

        } catch (error) {
            logger.error(error, "Unexpected error while generating Box upload reports");
        } finally {
            loadingContext.dispose();
            sceneViewer.restoreModuleSolarAccess?.();
        }

        return { files, failedReports };
    };

    const uploadAllFiles = async (
        salesforceId: string | null,
        files: Array<{ data?: Blob | null; name?: string | null }>,
        generationFailures: string[],
        designNumber: string,
        versionNumber: string,
        refreshDesignList: () => Promise<void>,
        onComplete: (uploaded: string[], failed: string[]) => void,
        onError: (error: unknown) => void,
    ): Promise<{ cancelledRemainingUploads: boolean }> => {
        const uploadedFiles: string[] = [];
        const notUploaded: string[] = [];
        let cancelledRemainingUploads = false;
        const loadingContext = new LoadingContext();
        loadingContext.setDefaultTitle("Uploading Files...");
        const uploadContext = { designNumber, versionNumber };

        const validFiles = files.filter(isValidBoxUploadFile);
        const invalidFiles = files.filter((file) => !isValidBoxUploadFile(file));

        invalidFiles.forEach((file) => {
            logger.error("Skipping invalid Box upload file before API call", {
                ...uploadContext,
                fileName: file.name ?? null,
                blobSize: file.data?.size ?? 0,
            });
            notUploaded.push(file.name ?? "Unknown file");
        });

        const fileStatuses: FileUploadStatus[] = validFiles.map((file) => ({
            name: file.name ?? "Unknown file",
            percent: 0,
            state: "uploading",
        }));

        let progressFrame = 0;
        const publishUploadProgress = (title?: string) => {
            const doneCount = fileStatuses.filter(
                (status) => status.state === "done" || status.state === "failed",
            ).length;
            const displayStatuses: FileUploadStatus[] = [
                ...generationFailures.map((name) => ({
                    name,
                    percent: 100,
                    state: "failed" as const,
                })),
                ...invalidFiles.map((file) => ({
                    name: file.name ?? "Unknown file",
                    percent: 100,
                    state: "failed" as const,
                })),
                ...fileStatuses,
            ];
            loadingContext.setMessage({
                title: title ?? `Uploading to Box (${doneCount}/${validFiles.length})`,
                message: renderBoxUploadFileList(displayStatuses),
            });
        };
        const scheduleUploadProgress = () => {
            if (progressFrame) return;
            progressFrame = requestAnimationFrame(() => {
                progressFrame = 0;
                publishUploadProgress();
            });
        };

        try {
            if (validFiles.length > 0) {
                publishUploadProgress();

                const uploadFile = async (
                    file: BoxUploadFile,
                    idx: number,
                    uploadFn: typeof uploadToBox,
                ) => {
                    const result = await uploadFn(
                        salesforceId as string,
                        file,
                        (progress: number) => {
                            fileStatuses[idx].percent = progress;
                            fileStatuses[idx].state = progress >= 100 ? "processing" : "uploading";
                            scheduleUploadProgress();
                        },
                        uploadContext,
                    );
                    fileStatuses[idx].percent = 100;
                    fileStatuses[idx].state = result ? "done" : "failed";
                    scheduleUploadProgress();
                    if (result) uploadedFiles.push(file.name);
                    else notUploaded.push(file.name);
                    return result;
                };

                const grayscaleIndex = validFiles.findIndex(isGrayscaleTopviewFile);
                const remainingIndexes = validFiles
                    .map((_, idx) => idx)
                    .filter((idx) => idx !== grayscaleIndex);

                if (grayscaleIndex >= 0) {
                    const grayscaleFile = validFiles[grayscaleIndex];
                    const grayscaleUploaded = await uploadFile(
                        grayscaleFile,
                        grayscaleIndex,
                        uploadToBoxDirectly,
                    );

                    if (!grayscaleUploaded) {
                        cancelledRemainingUploads = true;
                        remainingIndexes.forEach((idx) => {
                            fileStatuses[idx].percent = 100;
                            fileStatuses[idx].state = "failed";
                            notUploaded.push(validFiles[idx].name);
                        });
                        publishUploadProgress();
                        logger.error("Box grayscale direct upload failed; remaining Box uploads cancelled", {
                            salesforceId,
                            ...uploadContext,
                            grayscaleFile: grayscaleFile.name,
                            cancelledFiles: remainingIndexes.map((idx) => validFiles[idx].name),
                        });
                    }
                }

                if (!cancelledRemainingUploads) {
                    const indexesToUpload = grayscaleIndex >= 0
                        ? remainingIndexes
                        : validFiles.map((_, idx) => idx);
                    await Promise.all(
                        indexesToUpload.map((idx) => uploadFile(validFiles[idx], idx, uploadToBox)),
                    );
                }

                if (notUploaded.length > 0) {
                    logger.error("Box upload batch finished with upload failures", {
                        salesforceId,
                        ...uploadContext,
                        uploadedFiles,
                        notUploaded,
                        generationFailures,
                        cancelledRemainingUploads,
                    });
                } else if (generationFailures.length > 0) {
                    logger.warn("Box upload completed with report generation failures", {
                        ...uploadContext,
                        generationFailures,
                        uploadedFiles,
                        notUploaded,
                    });
                }
            }
        } catch (error) {
            onError(error);
        } finally {
            if (progressFrame) cancelAnimationFrame(progressFrame);
            try {
                const generateDirectLayout = usesOneDocDirectLayout()
                    && fileStatuses.find((status) => isGrayscaleTopviewFile(status))?.state === "done";
                if (generateDirectLayout) {
                    const siteAddress = useViewerStore.getState().siteAddress;
                    const directLayoutFileName = `${siteAddress} - Direct Layout.pdf`;
                    fileStatuses.push({
                        name: directLayoutFileName,
                        percent: 0,
                        state: "processing",
                    });
                    publishUploadProgress("Generating Direct Layout...");
                    const generated = await generateDirectLayoutInBox(
                        salesforceId as string,
                        siteAddress,
                    );
                    const directLayoutStatus = fileStatuses[fileStatuses.length - 1];
                    directLayoutStatus.percent = 100;
                    directLayoutStatus.state = generated ? "done" : "failed";
                    publishUploadProgress("Generating Direct Layout...");
                    if (generated) {
                        uploadedFiles.push(directLayoutFileName);
                    } else {
                        notUploaded.push(directLayoutFileName);
                    }
                }
                loadingContext.setMessage({
                    title: "Refreshing Design List...",
                    message: "File upload complete. Updating designs, please wait...",
                });
                await refreshDesignList();
                onComplete(uploadedFiles, notUploaded);
            } finally {
                useViewerStore.setState({ isSavingInProgress: false });
                loadingContext.dispose();
            }
        }

        return { cancelledRemainingUploads };
    };

    const saveEpcEstimate = (DesignNumber: string, VersionNumber: string) => {
        const sales_force_id = sessionStorage.getItem("SalesForceId");
        const { pricingType, ppwValue, multiplier, escalatorValue, epcValue, epcMessage } =
            useViewerStore.getState();

        const payload = {
            salesforce_Id: sales_force_id,
            pricing_Method: pricingType,
            pricing_Value: ppwValue,
            annual_Usage: annualUsageInput,
            multiplier,
            escalator: escalatorValue,
            design_Data: `Design:${DesignNumber}-Version:${VersionNumber}`,
            epC_PriceValue: epcValue,
            estimated_Production: epcEstimatedProductionInput,
            exception_Message: epcMessage,
        };

        return treeEPCService.saveEpcEstimation(payload);
    };


    return (
        <>
            {notReady ? (
                <>
                    <Alert
                        style={{ color: 'var(--textColor)' }}
                        message="Please wait, values are still being calculated"
                        type="info"
                        showIcon
                    />
                    <br />
                    <Flex style={{ textAlign: "center" }} gap={10} align="center" justify="center">
                        <span className="loader-icon-spinner " />
                        <p className="theme-based-text-color">
                            {calculationBlockedMessage ?? 'Please wait, values are still being calculated'}
                        </p>
                    </Flex>
                </>
            ) : saving ? (
                <>
                    <Alert
                        style={{ color: 'var(--textColor)' }}
                        message="This may take up to 30 seconds, don't refresh or quit while in progress."
                        type="info"
                        showIcon
                    />
                    <br />
                    <Flex style={{ textAlign: "center" }} gap={10} align="center" justify="center">
                        <span className="loader-icon-spinner " />
                        <p className="theme-based-text-color">
                            Saving New {designID === 0 || isInActiveDesign ? "Design" : "Version"} to the database. Please Wait...
                        </p>
                    </Flex>
                </>
            ) : (
                <>
                    <b>
                        <p className="report-text">
                            Are you sure you want to Finalize and Save the Design. Click Confirm to proceed.
                        </p>
                    </b>
                    <br />
                    <ul className="report-list finalize-modal__notes-list">
                    {

                        detachedStructureBuildingIds.length > 0 &&
                        <p>
                            <b>Note:</b> Following buildings are marked as Detached Structures:
                            <br/>
                            {
                                detachedStructureBuildingIds.map((buildingId : number,index) => (
                                    <span> Building {buildingId}{detachedStructureBuildingIds.length>1 && ","}</span>
                                ))
                            }
                        </p>
                    }

                    {isInActiveDesign ? (
                        <Alert
                            style={{ color: "black" }}
                            message="You are Re-finalizing an Inactive Design / Older Design . This will be treated as a New Design and will not be saved as a New Version."
                            type="warning"
                            showIcon
                        />
                    ) : null}

                    <div className="finalize-modal__fields-grid">
                        <div>
                            <div style={{ minHeight: 132 }}>
                                <p
                                    className="report-text"
                                    style={{ marginTop: 0, marginBottom: 8, fontWeight: "bold" }}
                                >
                                    Setback Notes:
                                </p>
                                <div
                                    style={{
                                        height: 100,
                                        overflowY: "auto",
                                        margin: 0,
                                        fontSize: 16,
                                        color: "var(--textColor)",
                                    }}
                                >
                                    {setbackNotes}
                                </div>
                            </div>

                            {hasCapability("RSA_Status") && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    <p className="report-text" style={{ marginTop: 0, marginBottom: 0, fontWeight: "bold" }}>
                                        RSA status:
                                    </p>
                                    <Select
                                        placeholder="RSA Status"
                                        options={rsaStatusOptions}
                                        value={rsaStatus}
                                        onChange={setRsaStatus}
                                        size="large"
                                        suffixIcon={<CaretDownIcon size={18} />}
                                    />
                                </div>
                            )}

                            {hasCapability("Direct_Layout") && (
                                <>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                        <p className="report-text" style={{ marginTop: 0, marginBottom: 0, fontWeight: "bold" }}>
                                            Annual usage:
                                        </p>
                                        <Input
                                            type="number"
                                            value={annualUsageInput ?? ''}
                                            onChange={handleAnnualUsageInputChange}
                                            placeholder="Enter Value Here in kWh"
                                        />
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                        <p className="report-text" style={{ marginTop: 0, marginBottom: 0, fontWeight: "bold" }}>
                                            Finance partner production:
                                        </p>
                                        <Input
                                            type="number"
                                            value={epcEstimatedProductionInput ?? undefined}
                                            onChange={(e) => setEpcEstimatedProductionInput(Number(e.target.value))}
                                            placeholder="Enter Value Here in kWh"
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        <div>
                            {hasCapability("Direct_Layout") ? (
                                <div style={{ flexShrink: 0, minHeight: 132 }}>
                                    <p className="report-text" style={{ marginTop: 0, marginBottom: 8, fontWeight: "bold" }}>
                                        Customer and Site-Specific restrictions:
                                    </p>
                                    <Input.TextArea
                                        value={customerSiteRestrictions}
                                        onChange={handleCustomerSiteRestrictionsChange}
                                        placeholder="Optional. Notes about design restrictions or site details from your customer conversation. Saved when you finalize."
                                        style={{ height: 100, resize: "none" }}
                                    />
                                </div>
                            ) : (
                                hasCapability("RSA_notes") &&
                                (hasCapability("RSA_Status") || hasCapability("Direct_Layout")) && (
                                    <div style={{ minHeight: 132 }} aria-hidden />
                                )
                            )}

                            {hasCapability("RSA_notes") && (
                                <div
                                    style={{
                                        flex: 1,
                                        minHeight: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 8,
                                    }}
                                >
                                    <p className="report-text" style={{ marginTop: 0, marginBottom: 0, fontWeight: "bold", flexShrink: 0 }}>
                                        RSA Notes:
                                    </p>
                                    <div style={{ flex: 1, minHeight: 140, display: "flex" }}>
                                        <Input.TextArea
                                            value={rsaNotes}
                                            onChange={handleRsaNotesChange}
                                            maxLength={255}
                                            autoSize={false}
                                            placeholder="Enter text (optional)"
                                            style={{ flex: 1, resize: "none", minHeight: 140 }}
                                            styles={{
                                                textarea: {
                                                    height: "100%",
                                                    minHeight: 140,
                                                },
                                            }}
                                        />
                                    </div>
                                    <span
                                        className="theme-based-text-color"
                                        style={{
                                            alignSelf: "flex-end",
                                            fontSize: 12,
                                            lineHeight: "20px",
                                            opacity: 0.75,
                                        }}
                                    >
                                        {rsaNotes.length} / 255
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                    </ul>

                    <div className="modal-bottom-action-button-container">
                        <Button
                            id="finaliseConfirmBtn"
                            type="primary"
                            size="large"
                            className="editor__control--finalize modal-confirm-button"
                            onClick={confirm}
                            disabled={hasCapability("RSA_Status") && !rsaStatus}
                        >
                            Confirm
                        </Button>

                        <Button
                            type="primary"
                            size="large"
                            danger
                            className="editor__control--finalize modal-cancel-button"
                            onClick={cancel}
                        >
                            Cancel
                        </Button>
                    </div>
                </>
            )}
        </>
    );
};

export default FinaliseConfirm;
